import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { storageService } from '../services/storageService';
import { callSubirSeguroNuevo } from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { entrarALaApp } from '../utils/volver';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MI SEGURO — la única parte de los papeles que el chofer puede renovar solo
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Petición del fundador (2-oct-2026): «necesito que la app pueda enviarle al
 * chofer aviso de vencimiento cuando se le esté venciendo el seguro. Esta es la
 * única sección que él puede modificar sin depender de hacer el proceso
 * nuevamente; luego yo la reviso a ver si coinciden con los datos anteriores».
 *
 * ── POR QUÉ EXISTE ESTA PANTALLA ─────────────────────────────────────────
 *
 * El seguro es el único documento del KYC que CADUCA. Sin esto, renovarlo
 * significaba repetir la verificación entera —cédula, selfie, licencia,
 * matrícula y cinco fotos del vehículo— cada año, y el servidor además rechaza
 * reenviar el KYC mientras hay una revisión abierta. O sea: no había camino.
 *
 * Y lo que había detrás era peor. El repaso diario
 * (`functions/src/seguro.ts`) apaga al chofer con el seguro vencido y las
 * reglas de Firestore le cierran los viajes. Pero la app no sabía NADA de ese
 * bloqueo: `seguroVencidoBloqueo` no aparecía en una sola línea de `src/`. El
 * chofer se encontraba apagado sin que nada le dijera por qué ni qué hacer.
 *
 * ── LO QUE ESTA PANTALLA NO HACE, Y SE DICE EN VOZ ALTA ──────────────────
 *
 * Subir la foto NO levanta el bloqueo ni mueve la fecha. La fecha la escribe
 * el administrador leyendo la póliza, y es deliberado: si la tecleara el
 * chofer, se pondría la que le conviniera y el candado no serviría de nada.
 * Decirlo aquí evita que vuelva diez veces a ver si ya se desbloqueó.
 */

type Perfil = {
  seguroVenceEl?: any;
  seguroVencidoBloqueo?: boolean;
  seguroPendienteUrl?: string | null;
  seguroPendienteEn?: any;
};

/** Timestamp de Firestore, Date, número o nada → milisegundos o null. */
function aMilisegundos(v: any): number | null {
  if (!v) return null;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const UN_DIA = 24 * 60 * 60 * 1000;

/**
 * TODA ESPERA DE RED, CON RELOJ.
 *
 * Una lectura de Firestore sin cobertura NO falla: se queda esperando y su
 * promesa puede no resolverse nunca. Sin esto, la pantalla se quedaba con el
 * indicador girando para siempre — comprobado en el navegador, no supuesto.
 * `map.tsx` ya hace lo mismo con esta misma lectura y por el mismo motivo.
 */
function conReloj<T>(promesa: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((ok, mal) => {
    const reloj = setTimeout(() => mal(new Error('tiempo agotado')), ms);
    promesa.then(
      (v) => { clearTimeout(reloj); ok(v); },
      (e) => { clearTimeout(reloj); mal(e); }
    );
  });
}

export default function SeguroScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [perfil, setPerfil] = React.useState<Perfil | null>(null);
  const [cargando, setCargando] = React.useState(true);
  const [subiendo, setSubiendo] = React.useState(false);
  const [fallo, setFallo] = React.useState(false);

  const cargar = React.useCallback(async () => {
    if (!uid) {
      setCargando(false);
      return;
    }
    setFallo(false);
    try {
      const me = await conReloj(firestoreService.getUserProfile(uid), 10000);
      setPerfil((me ?? {}) as Perfil);
    } catch (e: any) {
      // Que no se pueda LEER el estado no impide MANDAR la póliza: son dos
      // caminos distintos, y el que trae a esta pantalla es el segundo.
      console.warn('[seguro] no se pudo leer el perfil:', e?.message);
      setFallo(true);
    } finally {
      setCargando(false);
    }
  }, [uid]);

  // Al volver a la pantalla se relee: si el admin acaba de poner la fecha, lo
  // que se ve aquí tiene que ser lo de ahora y no lo de cuando se abrió.
  useFocusEffect(
    React.useCallback(() => {
      cargar();
    }, [cargar])
  );

  const venceMs = aMilisegundos(perfil?.seguroVenceEl);
  const bloqueado = perfil?.seguroVencidoBloqueo === true;
  const enRevision = !!perfil?.seguroPendienteUrl;
  const dias = venceMs != null ? Math.ceil((venceMs - Date.now()) / UN_DIA) : null;

  const subir = async () => {
    if (!uid) {
      Alert.alert('Sesión requerida', 'Vuelve a iniciar sesión e intenta de nuevo.');
      return;
    }
    try {
      // ⚠️ EL PERMISO SE PIDE ANTES DE ABRIR LA CÁMARA, Y NADA MÁS.
      // Pedir ADEMÁS el de galería rompe el gesto del usuario y en algunos
      // Android deja el selector sin abrir. Aquí solo hace falta la cámara: la
      // póliza se fotografía, no se busca en el carrete — así no se puede
      // mandar la foto de la póliza de otro guardada en el teléfono.
      const permiso = await ImagePicker.requestCameraPermissionsAsync();
      if (!permiso.granted) {
        Alert.alert(
          'Necesitamos la cámara',
          'Para enviar tu póliza hay que tomarle una foto. Activa el permiso de cámara en los ajustes del teléfono.'
        );
        return;
      }

      const foto = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        // La póliza es un papel: lo que importa es que se lea la FECHA, así que
        // la calidad no baja de 0.85 — con 0.5 el año sale borroso y el
        // administrador no puede teclear lo que no puede leer.
        quality: 0.85,
        base64: Platform.OS === 'web',
      });
      if (foto.canceled || !foto.assets?.[0]) return;

      setSubiendo(true);
      const url = await storageService.uploadKYCDocument(
        uid,
        'seguro_vigente',
        foto.assets[0].uri,
        foto.assets[0].base64 ?? null
      );
      await callSubirSeguroNuevo({ url });
      await cargar();
      Alert.alert(
        'Póliza enviada',
        'Ya la recibimos. La revisamos y ponemos la fecha de vencimiento que dice el documento — ' +
          'te avisamos por notificación en cuanto quede lista.'
      );
    } catch (e: any) {
      console.warn('[seguro] no se pudo enviar la póliza:', e?.message);
      Alert.alert(
        'No pudimos enviar la póliza',
        mensajeDeError(e, 'Revisa tu conexión y vuelve a tomar la foto.')
      );
    } finally {
      setSubiendo(false);
    }
  };

  // ── EL ESTADO, EN UNA FRASE ───────────────────────────────────────────────
  // El orden es el de urgencia: primero lo que te impide trabajar HOY.
  const estado = bloqueado
    ? {
        color: '#D32F2F',
        icono: 'close-circle' as const,
        titulo: 'Tu seguro está vencido',
        texto:
          'Mientras esté vencido no puedes aceptar viajes. Tómale una foto a tu póliza nueva y la revisamos.',
      }
    : enRevision
      ? {
          color: '#F57C00',
          icono: 'time' as const,
          titulo: 'Estamos revisando tu póliza',
          texto:
            'Ya la recibimos. Leemos la fecha de vencimiento del documento y la guardamos — te avisamos cuando esté.',
        }
      : venceMs == null
        ? {
            color: '#F57C00',
            icono: 'help-circle' as const,
            titulo: 'No tenemos la fecha de tu seguro',
            texto:
              'Sin fecha no podemos avisarte cuando se te venza. Tómale una foto a tu póliza para que la registremos.',
          }
        : dias != null && dias <= 30
          ? {
              color: '#F57C00',
              icono: 'alert-circle' as const,
              titulo: dias <= 0 ? 'Tu seguro vence hoy' : `Tu seguro vence en ${dias} día${dias === 1 ? '' : 's'}`,
              texto:
                'Renuévalo antes de esa fecha y mándanos la foto de la póliza nueva. Si llega el día sin renovar, ' +
                'se te apaga el perfil hasta que la recibamos.',
            }
          : {
              color: '#2E7D32',
              icono: 'shield-checkmark' as const,
              titulo: 'Tu seguro está al día',
              texto: 'Te avisaremos cuando falte un mes para que venza. No tienes que hacer nada ahora.',
            };

  const fecha = venceMs != null ? new Date(venceMs).toLocaleDateString('es-DO', {
    day: '2-digit', month: 'long', year: 'numeric',
  }) : null;

  return (
    <ScreenBackground>
      <PantallaSegura style={styles.safe}>
        <View style={[styles.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity
            onPress={() => (router.canGoBack() ? router.back() : entrarALaApp(router, '/(tabs)/profile'))}
            style={styles.volver}
            accessibilityRole="button"
            accessibilityLabel="Volver"
          >
            <Ionicons name="arrow-back" size={20} color={colors.primary} />
            <Text style={[styles.volverTexto, { color: colors.primary }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.cuerpo}>
          <Text style={[styles.titulo, { color: colors.text }]}>Mi seguro</Text>
          <Text style={[styles.ayuda, { color: colors.textSecondary }]}>
            Es el único documento que puedes renovar sin repetir toda la verificación.
          </Text>

          {cargando ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 40 }} />
          ) : (
            <>
              {fallo ? (
                <TouchableOpacity
                  onPress={() => { setCargando(true); cargar(); }}
                  style={[styles.tarjeta, { backgroundColor: colors.card, borderColor: '#F57C00' }]}
                  accessibilityRole="button"
                  accessibilityLabel="Reintentar la lectura del estado de tu seguro"
                >
                  <View style={styles.filaEstado}>
                    <Ionicons name="cloud-offline" size={28} color="#F57C00" />
                    <Text style={[styles.estadoTitulo, { color: colors.text }]}>
                      No pudimos leer el estado de tu seguro
                    </Text>
                  </View>
                  <Text style={[styles.estadoTexto, { color: colors.textSecondary }]}>
                    Revisa tu conexión y toca aquí para volver a intentarlo. Puedes enviar tu póliza
                    igual — eso sí nos llega.
                  </Text>
                </TouchableOpacity>
              ) : (
              <View style={[styles.tarjeta, { backgroundColor: colors.card, borderColor: estado.color }]}>
                <View style={styles.filaEstado}>
                  <Ionicons name={estado.icono} size={28} color={estado.color} />
                  <Text style={[styles.estadoTitulo, { color: colors.text }]}>{estado.titulo}</Text>
                </View>
                <Text style={[styles.estadoTexto, { color: colors.textSecondary }]}>{estado.texto}</Text>
                {fecha ? (
                  <Text style={[styles.fecha, { color: colors.textSecondary }]}>
                    Vence el <Text style={{ fontWeight: '700', color: colors.text }}>{fecha}</Text>
                  </Text>
                ) : null}
              </View>
              )}

              {perfil?.seguroPendienteUrl ? (
                <View style={[styles.tarjeta, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[styles.subtitulo, { color: colors.text }]}>La foto que enviaste</Text>
                  <Image
                    source={{ uri: perfil.seguroPendienteUrl }}
                    style={styles.foto}
                    resizeMode="contain"
                  />
                </View>
              ) : null}

              <TouchableOpacity
                onPress={subir}
                disabled={subiendo}
                style={[styles.boton, { backgroundColor: subiendo ? colors.border : colors.primary }]}
                accessibilityRole="button"
              >
                {subiendo ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  <>
                    <Ionicons name="camera" size={20} color="#FFF" />
                    <Text style={styles.botonTexto}>
                      {enRevision ? 'Enviar otra foto' : 'Tomar foto de mi póliza'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>

              {/* LO QUE SUBIR LA FOTO **NO** HACE. Se dice aquí, antes de que lo
                  pregunte: si no, sube la foto, se queda mirando el botón de
                  ponerse en línea y cree que la app está rota. */}
              <Text style={[styles.letraChica, { color: colors.textSecondary }]}>
                La foto sola no reactiva el perfil: una persona de nuestro equipo lee la fecha de la póliza
                y la registra. Es así para que nadie pueda ponerse una fecha que el documento no dice.
              </Text>
              <Text style={[styles.letraChica, { color: colors.textSecondary }]}>
                Que se vea completa y enfocada, sobre todo <Text style={{ fontWeight: '700' }}>la fecha de
                vencimiento</Text> y la <Text style={{ fontWeight: '700' }}>placa</Text>: tienen que ser las
                del vehículo que tienes aprobado.
              </Text>
            </>
          )}
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  cabecera: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  volver: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingRight: 12 },
  volverTexto: { fontSize: 16, marginLeft: 4, fontWeight: '600' },
  cuerpo: { padding: 16, paddingBottom: 40 },
  titulo: { fontSize: 24, fontWeight: '800', marginBottom: 4 },
  ayuda: { fontSize: 14, lineHeight: 20, marginBottom: 18 },
  tarjeta: { borderRadius: 14, borderWidth: 1, padding: 16, marginBottom: 14 },
  filaEstado: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  estadoTitulo: { fontSize: 17, fontWeight: '700', marginLeft: 10, flex: 1 },
  estadoTexto: { fontSize: 14, lineHeight: 20 },
  fecha: { fontSize: 14, marginTop: 10 },
  subtitulo: { fontSize: 15, fontWeight: '700', marginBottom: 10 },
  foto: { width: '100%', height: 220, borderRadius: 10, backgroundColor: 'rgba(127,127,127,0.12)' },
  boton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    paddingVertical: 15,
    marginTop: 4,
  },
  botonTexto: { color: '#FFF', fontSize: 16, fontWeight: '700', marginLeft: 8 },
  letraChica: { fontSize: 12.5, lineHeight: 18, marginTop: 14 },
});
